import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { friendService } from '../../services';
import FriendActionButton from '../FriendActionButton';
import { SearchIcon } from '../hand/icons';
import { tintFor } from '../../utils/tints';
import { readDismissedPeople } from '../PeopleYouMayKnow';
import { SectionHead } from '../pagesystem/PageSystem';

/**
 * FIND YOUR PEOPLE — guest dashboard handoff §1. A 48px search pill (2+ characters, 300ms
 * debounce → friendService.searchPeople, rows with FriendActionButton) and, with no query,
 * PEOPLE YOU MAY KNOW as a horizontal rail of 172px tiles: ×, a 76px photo, the name,
 * `@HANDLE · CITY`, mutual faces + `N MUTUAL`, an optional club tag and a full-width + ADD
 * that turns cyan `SENT ✓`. Dismissals share PeopleYouMayKnow's localStorage key so a
 * person hidden here stays hidden on /people too.
 */
const DISMISS_KEY = 'pr.people.dismissed';
const profilePath = (p) => (p.handle ? `/${p.handle}` : `/users/${p.userId}`);
const metaOf = (p) => [p.handle ? `@${p.handle}` : '', p.location || ''].filter(Boolean).join(' · ').toUpperCase();

function Face({ person, size }) {
  return (
    <span style={{ background: tintFor(person.userId || person.displayName) }} title={person.displayName}>
      {person.avatarUrl ? <img src={person.avatarUrl} alt="" referrerPolicy="no-referrer" /> : (person.displayName || '?')[0]}
    </span>
  );
}

export default function FindYourPeople({ limit = 6, onSay }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(readDismissedPeople);
  const [leaving, setLeaving] = useState(() => new Set());
  const [sent, setSent] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    friendService.getSuggestedFriends(limit).then((r) => {
      if (cancelled) return;
      if (r?.success) setPeople(r.data || []);
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [limit]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); setSearching(false); return undefined; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      const r = await friendService.searchPeople(term);
      if (cancelled) return;
      setResults(r?.success ? (r.data || []) : []);
      setSearching(false);
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const add = async (person) => {
    if (sent.has(person.userId) || person.requestSent) return;
    const r = await friendService.sendFriendRequest(person.userId);
    if (!r?.success) { onSay?.(r?.error || 'COULD NOT SEND THAT REQUEST.'); return; }
    setSent((prev) => new Set(prev).add(person.userId));
    onSay?.(`REQUEST SENT TO ${(person.displayName || '').toUpperCase()}`);
  };

  const hide = (person) => {
    setLeaving((prev) => new Set(prev).add(person.userId));
    setTimeout(() => {
      setDismissed((prev) => {
        const next = new Set(prev);
        next.add(person.userId);
        try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...next])); } catch { /* storage disabled */ }
        return next;
      });
      setLeaving((prev) => { const next = new Set(prev); next.delete(person.userId); return next; });
    }, 200);
  };

  const rail = people.filter((p) => !dismissed.has(p.userId));
  const isSearch = results !== null || q.trim().length >= 2;

  return (
    <section className="gd-people" aria-labelledby="gd-people-h">
      <SectionHead id="gd-people-h" title="FIND YOUR PEOPLE" detail={isSearch ? 'SEARCH RESULTS' : 'PEOPLE YOU MAY KNOW · BY MUTUAL FRIENDS'} link="ALL →" onLink={() => navigate('/friends')} />
      <div className="gd-people-panel">
        <label className="gd-search">
          <SearchIcon size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or @handle…"
            aria-label="Search people"
            autoComplete="off"
            data-1p-ignore="true"
          />
          {q && <button type="button" onClick={() => setQ('')} aria-label="Clear">CLEAR</button>}
        </label>

        {isSearch ? (
          <div className="gd-results" data-testid="people-results">
            {searching && (results || []).length === 0 && <span className="gd-people-empty">SEARCHING…</span>}
            {!searching && results && results.length === 0 && <span className="gd-people-empty">NO ONE BY THAT NAME.</span>}
            {(results || []).map((p) => (
              <div key={p.userId} className="gd-result">
                <button type="button" className="gd-result-who" onClick={() => navigate(profilePath(p))}>
                  <Face person={p} />
                  <span className="gd-result-text">
                    <b>{p.displayName}</b>
                    {metaOf(p) && <span>{metaOf(p)}</span>}
                  </span>
                </button>
                <FriendActionButton
                  status={p.relationshipStatus}
                  requestSent={sent.has(p.userId) || p.requestSent}
                  onAdd={() => add(p)}
                  onRespond={() => navigate('/friends')}
                  userId={p.userId}
                  isClose={p.isClose}
                  name={p.displayName}
                />
              </div>
            ))}
          </div>
        ) : loading ? (
          <span className="gd-people-empty">FINDING PEOPLE…</span>
        ) : rail.length === 0 ? (
          <span className="gd-people-empty">{people.length ? "THAT'S EVERYONE FOR NOW." : 'NO SUGGESTIONS YET.'}</span>
        ) : (
          <div className="gd-rail" data-testid="people-rail">
            {rail.map((p) => {
              const isFriend = p.relationshipStatus === 'friends';
              const isSent = sent.has(p.userId) || p.requestSent || p.relationshipStatus === 'request_sent';
              const mutuals = p.mutuals || [];
              return (
                <div key={p.userId} className="gd-person" data-leaving={leaving.has(p.userId) ? '' : undefined}>
                  <button type="button" className="gd-hide" onClick={() => hide(p)} aria-label={`Not now: ${p.displayName}`}>×</button>
                  <button type="button" className="gd-person-photo" style={{ background: tintFor(p.userId) }} onClick={() => navigate(profilePath(p))} aria-label={p.displayName}>
                    {p.avatarUrl ? <img src={p.avatarUrl} alt="" referrerPolicy="no-referrer" /> : (p.displayName || '?')[0]}
                  </button>
                  <b>{p.displayName}</b>
                  <small>{metaOf(p) || 'NEW HERE'}</small>
                  <span className="gd-mutual">
                    {mutuals.length > 0 && (
                      <span className="gd-mutual-faces">{mutuals.slice(0, 3).map((m, i) => <Face key={m.userId || i} person={m} />)}</span>
                    )}
                    <span>{p.mutualCount ? `${p.mutualCount} MUTUAL` : (p.sharedEventTitle ? 'SAME EVENT' : 'NEW HERE')}</span>
                  </span>
                  {p.mutualClubName && <span className="gd-club">{p.mutualClubName.toUpperCase()}{p.mutualClubCount > 1 ? ` +${p.mutualClubCount - 1}` : ''}</span>}
                  <button
                    type="button"
                    className="gd-add"
                    data-sent={isSent && !isFriend ? '' : undefined}
                    data-friends={isFriend ? '' : undefined}
                    disabled={isSent || isFriend}
                    onClick={() => add(p)}
                  >
                    {isFriend ? 'FRIENDS' : isSent ? 'SENT ✓' : '+ ADD'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
